import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";

import { ProjectContext } from "@/stores/project-provider";
import { getAllParentPaths } from "@/utils/get-file-parent-path";

import { invalidateIfOutdated } from "./hooks/if-outdated";
import { useDebounceInvalidation } from "./hooks/use-debounce-invalidation";

// Single

const getFileHistoryQuery = (projectId: string, path: string | undefined) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["project", projectId, "path", path, "history"],
    queryFn: async () =>
      await getRanaApiV1().FilesApi.projectFileHistory({
        path,
        projectId,
        limit: 50,
      }),
    enabled: path !== undefined,
  });
};

export const useFileHistoryQuery = (path: string | undefined) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQuery(getFileHistoryQuery(projectId, path));
};

export const useFileHistoryQueries = (paths: string[]) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQueries({ queries: paths.map((path) => getFileHistoryQuery(projectId, path)) });
};

// INVALIDATION

// IMPORTANT: never call this directly. Always use `useInvalidateFilesAndHistoryQueriesIfOutdated`
export const useInvalidateFileHistoryQueriesIfOutdated = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();

  return useDebounceInvalidation((finishedAt: Date, path: string) => {
    invalidateIfOutdated(queryClient, finishedAt, getFileHistoryQuery(projectId, path).queryKey);
    getAllParentPaths(path).forEach((parentPath) => {
      invalidateIfOutdated(
        queryClient,
        finishedAt,
        getFileHistoryQuery(projectId, parentPath).queryKey
      );
    });
  });
};

// IMPORTANT: never call this directly. Always use `useInvalidateFilesAndHistoryQueries`
export const useInvalidateFileHistoryQueries = () => {
  const queryClient = useQueryClient();
  // Cant get project id from context because otherwise it wont refresh correctly in file uploading provider
  return async (projectId: string, path: string) => {
    await queryClient.invalidateQueries({
      queryKey: getFileHistoryQuery(projectId, path).queryKey,
      refetchType: "all",
    });
    getAllParentPaths(path).forEach(async (parentPath) => {
      await queryClient.invalidateQueries({
        queryKey: getFileHistoryQuery(projectId, parentPath).queryKey,
        refetchType: "all",
      });
    });
  };
};
